/**
 * PrecastSolver — geometry builder for the Precast tab renderers.
 *
 * Turns the rule-driven precast element data (generated panels, embedded
 * mounting parts, reinforcement meshes) into THREE meshes that the view
 * layer renders as normal BIM elements. Reinforcement bars are merged into a
 * single mesh (like RebarSolver) so a 600-bar panel stays one draw call.
 */
import * as THREE from 'three';
import { mergeGeometries } from '../rendering/instancing/mergeGeometries';
import {
    PrecastReinforcementElement,
    PrecastMountingPartElement,
    PrecastMountingKind,
} from '../core/types';

/** One bar cylinder, oriented from start to end (meters), radius from mm. */
function barGeometry(start: [number, number, number], end: [number, number, number], diameterMm: number): THREE.BufferGeometry {
    const s = new THREE.Vector3(...start);
    const e = new THREE.Vector3(...end);
    const length = Math.max(s.distanceTo(e), 0.001);
    const radius = Math.max(diameterMm / 1000 / 2, 0.0015);
    const geo = new THREE.CylinderGeometry(radius, radius, length, 6);
    // Cylinder axis is +Y; rotate onto the bar direction, then center it.
    const dir = e.clone().sub(s).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    geo.applyQuaternion(quat);
    const mid = s.clone().add(e).multiplyScalar(0.5);
    geo.translate(mid.x, mid.y, mid.z);
    return geo;
}

/**
 * Merged tube mesh for a precast reinforcement element: every bar as one
 * cylinder, all welded into a single BufferGeometry. Returns null when there
 * is nothing to draw so the renderer can bail out cheaply.
 */
export function buildReinforcementMesh(reinf: PrecastReinforcementElement): THREE.BufferGeometry | null {
    const bars = reinf.geometryData?.bars;
    if (!Array.isArray(bars) || bars.length === 0) return null;
    const geos = bars.map(b => barGeometry(b.start, b.end, reinf.geometryData.wireDiameter));
    const merged = mergeGeometries(geos);
    geos.forEach(g => g.dispose());
    return merged;
}

/**
 * Mesh for a mounting part, shaped by its kind:
 *  - plates → flat box flush to the face normal
 *  - loops/handles → open torus standing proud of the concrete
 *  - anchors/inserts/shear keys → short cylinder along the embedment axis
 * Local frame: Z = host normal (out of the face), so the part embeds along -Z.
 */
export function buildMountingPartMesh(part: PrecastMountingPartElement): { geometry: THREE.BufferGeometry; orient: THREE.Quaternion; color: string } {
    const g = part.geometryData;
    const sizeM = Math.max(g.size / 1000, 0.02);
    const normal = new THREE.Vector3(...g.normal).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

    let geometry: THREE.BufferGeometry;
    let color: string;
    switch (g.kind) {
        case 'Insert Plate':
            geometry = new THREE.BoxGeometry(sizeM, sizeM, Math.max(sizeM * 0.12, 0.006));
            geometry.translate(0, 0, -sizeM * 0.06);
            color = '#8a8f96';
            break;
        case 'Handling Loop':
            geometry = new THREE.TorusGeometry(sizeM * 0.5, Math.max(sizeM * 0.08, 0.006), 8, 16, Math.PI * 1.25);
            geometry.rotateZ(Math.PI * 0.375);
            color = '#c8641e';
            break;
        case 'Lifting Anchor':
            geometry = new THREE.ConeGeometry(sizeM * 0.35, sizeM, 12);
            geometry.rotateX(Math.PI / 2);
            geometry.translate(0, 0, sizeM * 0.2);
            color = '#d9822b';
            break;
        case 'Shear Key':
            geometry = new THREE.BoxGeometry(sizeM * 0.6, sizeM * 0.3, sizeM * 0.8);
            geometry.translate(0, 0, -sizeM * 0.4);
            color = '#7a828c';
            break;
        case 'Threaded Insert':
        case 'Bolt Anchor':
        default:
            geometry = new THREE.CylinderGeometry(sizeM * 0.3, sizeM * 0.3, sizeM * 0.9, 12);
            geometry.rotateX(Math.PI / 2);
            geometry.translate(0, 0, -sizeM * 0.45);
            color = g.kind === 'Threaded Insert' ? '#9aa7b2' : '#c8641e';
            break;
    }
    return { geometry, orient: quat, color };
}

/** Kind → human label used in tooltips / schedules. */
export const PRECAST_MOUNTING_KIND_LABELS: Record<PrecastMountingKind, string> = {
    'Lifting Anchor': 'Lifting Anchor',
    'Handling Loop': 'Handling Loop',
    'Insert Plate': 'Insert Plate',
    'Bolt Anchor': 'Bolt Anchor',
    'Threaded Insert': 'Threaded Insert',
    'Shear Key': 'Shear Key',
};
